import { createContext, useContext, useEffect, useState, useCallback } from 'react';

const ThemeContext = createContext(undefined);

const STORAGE_KEY = 'isource-suite-theme';

function getInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (_) {
    /* localStorage unavailable */
  }
  return 'dark';
}

export function ThemeProvider({ children, scopeClass = 'new-admin-dash' }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    const root = document.documentElement;
    const scopes = document.getElementsByClassName(scopeClass);
    if (theme === 'light') {
      root.classList.add('theme-light');
      for (let i = 0; i < scopes.length; i++) {
        scopes[i].classList.add('theme-light');
      }
    } else {
      root.classList.remove('theme-light');
      for (let i = 0; i < scopes.length; i++) {
        scopes[i].classList.remove('theme-light');
      }
    }

    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* ignore */
    }
  }, [theme, scopeClass]);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isLight: theme === 'light' }}>
      {children}
    </ThemeContext.Provider>
  );
}

export default function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;
  // No ThemeProvider above us in React tree (e.g. EmployeeLandingPage mounted
  // standalone on /employee route). Fall back to a safe singleton default:
  // read persisted theme from localStorage (if any), default to dark.
  // We intentionally do NOT throw — otherwise pages that compose
  // EmployeeLandingPage outside NewAdminDashboard crash at render.
  const fallbackTheme = getInitialTheme();
  return {
    theme: fallbackTheme,
    isLight: fallbackTheme === 'light',
    toggleTheme: () => {
      // If someone calls toggleTheme without a provider: flip localStorage
      // directly and force a reload to re-read the fallback.
      try {
        const current = localStorage.getItem(STORAGE_KEY) || fallbackTheme;
        const next = current === 'dark' ? 'light' : 'dark';
        localStorage.setItem(STORAGE_KEY, next);
      } catch (_) { /* ignore */ }
      try { window.location.reload(); } catch (_) { /* ignore */ }
    },
  };
}
